import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { NgClass } from '@angular/common';
import { ModalComponent } from '../../../../shared/components/modal/modal';
import { TaxonomyService } from '../../../../core/services/taxonomy.service';
import { ToastService } from '../../../../core/services/toast.service';
import { SituationTaxonomy, ResponseTaxonomy } from '../../../../shared/models/taxonomy.models';
import { apiErrorMessage } from '../../../../shared/utils/api-error';

type TaxonomyDetail = SituationTaxonomy | ResponseTaxonomy;

@Component({
  selector: 'app-edit-taxonomy-modal',
  imports: [ModalComponent, NgClass],
  templateUrl: './edit-taxonomy-modal.html',
})
export class EditTaxonomyModalComponent implements OnInit {
  taxonomy = input.required<TaxonomyDetail>();
  type = input.required<'situation' | 'response'>();
  closed = output<void>();
  saved = output<void>();
  removed = output<void>();

  private taxonomyService = inject(TaxonomyService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  private initialName = '';
  private initialIsMultiItem = true;

  protected name = signal('');
  protected isMultiItem = signal(true);
  protected showErrors = signal(false);
  protected submitting = signal(false);
  protected removing = signal(false);
  protected confirmRemove = signal(false);
  protected hasChanges = computed(
    () => this.name().trim() !== this.initialName || this.isMultiItem() !== this.initialIsMultiItem,
  );

  ngOnInit(): void {
    const t = this.taxonomy();
    const name =
      this.type() === 'situation'
        ? (t as SituationTaxonomy).name
        : (t as ResponseTaxonomy).taxonomyName;
    this.initialName = name;
    this.initialIsMultiItem = t.isMultiItem;
    this.name.set(name);
    this.isMultiItem.set(t.isMultiItem);
  }

  protected onRemove(): void {
    if (!this.confirmRemove()) {
      this.confirmRemove.set(true);
      return;
    }
    this.removing.set(true);
    const t = this.taxonomy();
    const request$ =
      this.type() === 'situation'
        ? this.taxonomyService.deleteSituationTaxonomy(t.id)
        : this.taxonomyService.deleteResponseTaxonomy(t.id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Taxonomy deleted successfully');
        this.removed.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to delete taxonomy'), 'error');
        this.removing.set(false);
        this.confirmRemove.set(false);
      },
    });
  }

  protected onSubmit(): void {
    this.showErrors.set(true);
    if (!this.name().trim()) return;

    this.submitting.set(true);
    const t = this.taxonomy();
    const request$: Observable<unknown> =
      this.type() === 'situation'
        ? this.taxonomyService.updateSituationTaxonomy({
            id: t.id,
            name: this.name().trim(),
            isMultiItem: this.isMultiItem(),
          } as SituationTaxonomy)
        : this.taxonomyService.updateResponseTaxonomy({
            id: t.id,
            taxonomyName: this.name().trim(),
            isMultiItem: this.isMultiItem(),
          } as ResponseTaxonomy);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Taxonomy updated successfully');
        this.saved.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to update taxonomy'), 'error');
        this.submitting.set(false);
      },
    });
  }
}
